import { memo, type FC, useCallback } from 'react';

import { useGetPostCreationOptions } from '@/features/api/hooks/grapevine-post/useGetPostCreationOptions';
import { useMeinOrtTab } from '@/features/mein-ort/components/MeinOrtTabContext';
import {
  MeinOrtChannel,
  MeinOrtTab
} from '@/features/mein-ort/components/meinOrtTypes';
import { PostCreationContext } from '@/features/post-editor/types/postCreationOptions';
import { getAvailableSenderOptions } from '@/features/post-editor/utils/postCreationOptions';
import { PostFeedType } from '@/features/posts/types/postFeed';
import { useVerification } from '@/features/verification/providers/useVerification';
import { useAppNavigation } from '@/navigation/hooks/useAppNavigation';
import { AppRoute } from '@/navigation/types/navigation';
import { FloatingActionButton } from '@/shared/components/FloatingActionButton';

const CHANNEL_TYPE: Partial<Record<MeinOrtTab, PostFeedType | undefined>> = {
  [MeinOrtTab.NEWS]: PostFeedType.NEWS_ITEM,
  [MeinOrtTab.PLAUSCH]: PostFeedType.GOSSIP,
  [MeinOrtTab.MARKT]: PostFeedType.MARKET,
  [MeinOrtTab.EVENTS]: PostFeedType.HAPPENING
};
export const MeinOrtCreatePostFab: FC = memo(() => {
  const navigation = useAppNavigation();
  const { ensureVerified } = useVerification();
  const { activeTab, activeChannel } = useMeinOrtTab();

  const channelType = CHANNEL_TYPE[activeTab];
  const { data: options } = useGetPostCreationOptions(
    PostCreationContext.MEIN_ORT,
    activeChannel === MeinOrtChannel.FUNKZENTRALE
  );
  const canCreate = getAvailableSenderOptions(options).some((sender) =>
    sender.channels.some(
      (channel) =>
        channelType === undefined || channel.channelType === channelType
    )
  );

  const onPress = useCallback(() => {
    if (!ensureVerified()) {
      return;
    }
    navigation.navigate(AppRoute.PostCreationHome, {
      channelType,
      creationContext: PostCreationContext.MEIN_ORT
    });
  }, [navigation, ensureVerified, channelType]);

  if (activeChannel !== MeinOrtChannel.FUNKZENTRALE) {
    return;
  }

  if (!canCreate) {
    return;
  }

  return (
    <FloatingActionButton testID="mein-ort-create-post-fab" onPress={onPress} />
  );
});
MeinOrtCreatePostFab.displayName = 'MeinOrtCreatePostFab';
